/**
 * AttivitaDetailModal — vista di dettaglio READ-ONLY di una attività.
 * Apertura: click sul nome nella tabella Gantt o sull'indicatore Nota importante.
 * Mostra tutti i campi rilevanti + bottone "Modifica" per aprire il form completo.
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/api";
import { STATO_LABELS_GANTT } from "./ganttConstants";

export default function AttivitaDetailModal({ attivita, cantiere, onClose, onEdit }) {
  const a = attivita || {};
  const { data: lavoratori = [] } = useQuery({
    queryKey: ["lavoratori-attivi"],
    queryFn: () => api.get("/lavoratori").then((r) => r.data),
    staleTime: 60_000,
  });
  const assegnati = (a.lavoratori_assegnati || [])
    .map((id) => lavoratori.find((l) => l.lavoratore_id === id))
    .filter(Boolean);
  const statoLabel = STATO_LABELS_GANTT[a.stato] || a.stato || "—";
  const colore = a.colore_stato || "#94a3b8";
  const isRossa = !!a.nota_importante_rossa;

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="attivita-detail-modal"
    >
      <div
        className="bg-white rounded-md shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="p-5 border-b border-slate-200 sticky top-0 bg-white"
          style={{ borderLeft: `6px solid ${colore}` }}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
                Attività · Dettaglio
              </div>
              <h2 className="font-display text-xl font-black text-slate-900 flex items-center gap-2 flex-wrap">
                {a.nota_importante && (
                  <span
                    title={a.nota_importante}
                    className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-black ${isRossa ? "bg-rose-600 animate-pulse" : "bg-amber-500"}`}
                  >
                    ⚠️
                  </span>
                )}
                <span className="truncate">{a.nome || "Senza nome"}</span>
              </h2>
              {cantiere?.nome && (
                <div className="text-xs text-slate-500 mt-1">
                  🏗️ {cantiere.nome}{cantiere.cliente ? ` · ${cantiere.cliente}` : ""}
                </div>
              )}
            </div>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded text-slate-500 text-xl leading-none" aria-label="Chiudi" data-testid="btn-close-detail-att">✕</button>
          </div>
        </div>

        <div className="p-5 space-y-4 text-sm">
          {a.nota_importante && (
            <div className={`p-3 rounded-md border ${isRossa ? "bg-rose-50 border-rose-300 text-rose-900" : "bg-amber-50 border-amber-300 text-amber-900"}`}>
              <div className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${isRossa ? "text-rose-700" : "text-amber-800"}`}>
                {isRossa ? "⚠️ NOTA IMPORTANTE (URGENTE)" : "Nota importante"}
              </div>
              <div className={`text-sm ${isRossa ? "font-bold" : "font-semibold"} whitespace-pre-wrap`}>
                {a.nota_importante}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <DetailField label="Categoria" value={a.categoria || "—"} />
            <DetailField label="Stato">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-semibold border" style={{ background: colore + "22", color: colore, borderColor: colore + "66" }}>
                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: colore }} />
                {statoLabel}
              </span>
            </DetailField>
            <DetailField label="Data inizio prevista" value={a.data_inizio || "—"} mono />
            <DetailField label="Data fine prevista" value={a.data_fine || "—"} mono />
            {(a.ora_inizio || a.ora_fine) && (
              <DetailField label="Orario">
                <span className="font-mono text-sm">
                  {a.ora_inizio || "—"} → {a.ora_fine || "—"}
                </span>
              </DetailField>
            )}
            {a.data_inizio_effettiva && (
              <DetailField label="Inizio effettivo" value={a.data_inizio_effettiva} mono />
            )}
            {a.data_fine_effettiva && (
              <DetailField label="Fine effettiva" value={a.data_fine_effettiva} mono />
            )}
            <DetailField label="Responsabile" value={a.responsabile || "—"} />
            <DetailField label="Squadra / Fornitore" value={a.squadra_fornitore || "—"} />
            <DetailField label="Importo previsto" value={a.importo > 0 ? `€ ${Number(a.importo).toFixed(2)}` : "—"} mono />
            <DetailField label="Data fatturazione prevista" value={a.data_fatturazione_prevista || "—"} mono />
            <DetailField label="% completamento" value={`${a.percentuale_avanzamento || 0}%`} mono />
            {typeof a.durata_prevista_giorni === "number" && (
              <DetailField label="Durata prevista" value={`${a.durata_prevista_giorni} gg`} mono />
            )}
          </div>

          <DetailField label={`Persone assegnate (${assegnati.length})`}>
            {assegnati.length === 0 ? (
              <span className="text-slate-400 italic">Nessuna persona assegnata</span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {assegnati.map((l) => (
                  <span
                    key={l.lavoratore_id}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded-sm text-xs font-semibold text-emerald-900"
                  >
                    👤 {l.nome_completo || l.nome}
                    {l.ruolo && <span className="text-emerald-700 text-[10px]">· {l.ruolo}</span>}
                  </span>
                ))}
              </div>
            )}
          </DetailField>

          {a.attesa_materiale && (a.attesa_materiale.materiale_atteso || a.attesa_materiale.fornitore) && (
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-md">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
                📦 Materiali collegati
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                {a.attesa_materiale.materiale_atteso && (
                  <DetailField label="Materiale atteso" value={a.attesa_materiale.materiale_atteso} />
                )}
                {a.attesa_materiale.fornitore && (
                  <DetailField label="Fornitore" value={a.attesa_materiale.fornitore} />
                )}
                {a.attesa_materiale.data_ordine && (
                  <DetailField label="Data ordine" value={a.attesa_materiale.data_ordine} mono />
                )}
                {a.attesa_materiale.data_prevista_consegna && (
                  <DetailField label="Consegna prevista" value={a.attesa_materiale.data_prevista_consegna} mono />
                )}
                {a.attesa_materiale.numero_ordine && (
                  <DetailField label="N. ordine" value={a.attesa_materiale.numero_ordine} />
                )}
              </div>
            </div>
          )}

          {a.note && (
            <DetailField label="Note">
              <div className="text-slate-800 whitespace-pre-wrap">{a.note}</div>
            </DetailField>
          )}

          {a.evento_id && (
            <div className="text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded-sm px-2 py-1.5">
              📅 Sincronizzata con il calendario aziendale
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 sticky bottom-0 flex justify-between gap-2">
          <button
            onClick={onClose}
            data-testid="btn-close-detail-att-footer"
            className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-sm hover:bg-white"
          >
            Chiudi
          </button>
          <button
            onClick={onEdit}
            data-testid="btn-open-edit-from-detail"
            className="px-4 py-2 text-sm font-bold bg-orange-600 hover:bg-orange-700 text-white rounded-sm"
          >
            ✎ Modifica attività
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailField({ label, value, children, mono = false }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-0.5">{label}</div>
      <div className={mono ? "font-mono text-xs text-slate-900" : "text-sm text-slate-900"}>
        {children ?? value}
      </div>
    </div>
  );
}
